import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import {
  type ComponentPropsWithoutRef,
  type HTMLAttributes,
  forwardRef,
} from 'react'

import { cn } from '@/lib/utils'

/**
 * Dialog / modal — Radix primitives, shadcn-style chrome.
 *
 * Why Radix
 * ---------
 * Reliable focus trapping, ESC-to-close, click-outside-to-close,
 * scroll lock, and ARIA wiring. None of those are easy to get
 * right by hand and we do NOT want to reinvent them. The
 * components below are thin wrappers that drop our visual
 * conventions on top.
 *
 * Re-exports
 * ----------
 * `Dialog`, `DialogTrigger`, `DialogClose` come straight from
 * Radix because they're zero-styling primitives. We only style
 * the visible surfaces (Overlay + Content) and provide
 * Header/Title/Description/Footer slots so call sites read like
 * shadcn examples.
 */

export const Dialog = DialogPrimitive.Root
export const DialogTrigger = DialogPrimitive.Trigger
export const DialogClose = DialogPrimitive.Close
export const DialogPortal = DialogPrimitive.Portal

export const DialogOverlay = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(function DialogOverlay({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Overlay
      ref={ref}
      className={cn(
        'fixed inset-0 z-50 bg-black/40',
        'data-[state=open]:animate-in data-[state=closed]:animate-out',
        'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
        className,
      )}
      {...props}
    />
  )
})

export const DialogContent = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    /** Maximum width — defaults to `max-w-lg` (32rem). */
    size?: 'sm' | 'md' | 'lg' | 'xl'
  }
>(function DialogContent({ className, size = 'md', children, ...props }, ref) {
  const sizeClass: Record<NonNullable<typeof size>, string> = {
    sm: 'max-w-sm',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
  }
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
          'w-[calc(100vw-2rem)] max-h-[calc(100vh-4rem)] overflow-y-auto',
          'bg-surface rounded-lg border border-surface-border shadow-xl',
          'p-0',
          sizeClass[size],
          className,
        )}
        {...props}
      >
        {children}
        <DialogPrimitive.Close
          aria-label="Fermer"
          className={cn(
            'absolute right-3 top-3 rounded-md p-1.5',
            'text-text-muted hover:bg-surface-hover hover:text-text',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand',
          )}
        >
          <X size={18} aria-hidden="true" />
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  )
})

export function DialogHeader({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('px-6 pt-6 pb-3 flex flex-col gap-1.5', className)}
      {...props}
    />
  )
}

export function DialogBody({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('px-6 py-4', className)} {...props} />
}

export function DialogFooter({
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        'px-6 py-4 border-t border-surface-border',
        'flex flex-col-reverse sm:flex-row sm:justify-end gap-2',
        className,
      )}
      {...props}
    />
  )
}

export const DialogTitle = forwardRef<
  HTMLHeadingElement,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(function DialogTitle({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Title
      ref={ref}
      className={cn('text-lg font-semibold text-text', className)}
      {...props}
    />
  )
})

export const DialogDescription = forwardRef<
  HTMLParagraphElement,
  ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(function DialogDescription({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Description
      ref={ref}
      className={cn('text-sm text-text-muted', className)}
      {...props}
    />
  )
})
